/*
 * Header behaviour: discount codes, cart drawer, search, navigation drawers,
 * mega menus and sticky behaviour. Moved out of snippets/header-js.liquid so it
 * is cached and deferred instead of shipping ~70 KB of inline script in every
 * page's HTML. Liquid values (routes, money format, translated strings) come
 * from the JSON config that snippet renders (#mkt-header-config).
 */
(() => {
  'use strict';

  const runHeader = () => {
    const configEl = document.getElementById('mkt-header-config');
    if (!configEl) return;
    const CONFIG = JSON.parse(configEl.textContent);
    const ROUTES = CONFIG.routes;
    // The `t` filter HTML-escapes its output ("can&#39;t"). Decode once here:
    // messages are set with textContent, and markup built from them goes
    // through escapeHtml().
    const decoder = document.createElement('textarea');
    const TEXT = Object.fromEntries(Object.entries(CONFIG.text).map(([key, value]) => {
      decoder.innerHTML = value;
      return [key, decoder.value];
    }));


    // ── Discount codes ─────────────────────────────────────────
    // /cart/update.js takes `discount` as the comma-separated list of codes the
    // cart should carry, so applying a code sends the existing codes plus the new
    // one and removing a code sends the rest (an empty string clears them all).
    // Codes are read from the live cart first: a code added through a shareable
    // /discount/<code> link is never silently dropped by a later change.
    const DISCOUNT_TEXT = {
      applying: TEXT.discountApplying,
      applied: TEXT.discountApplied,
      pending: TEXT.discountPending,
      error: TEXT.discountError,
      alreadyApplied: TEXT.discountAlreadyApplied,
      removing: TEXT.discountRemoving,
      removed: TEXT.discountRemoved,
      removeError: TEXT.discountRemoveError,
      remove: TEXT.discountRemove,
      atCheckout: TEXT.discountAtCheckout,
    };
    const DISCOUNT_STATUS_KEY = 'cart-discount-status';
    // Only for /discount/, which the routes object does not publish. Everything
    // else here uses routes directly, because Liquid does render in this file.
    const shopRoot = () => window.Shopify?.routes?.root || '/';
    const sameCode = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
    // Only codes Shopify accepted. A rejected or no-longer-eligible code can sit
    // on the cart (from an old session or a /discount/<code> link); it is not
    // shown, and the next apply or remove drops it.
    const cartCodes = (cart) => (cart?.discount_codes || []).filter((entry) => entry.applicable).map((entry) => String(entry.code));
    const isCodeApplication = (application) => application?.type === 'discount_code';

    // A discount amount in cents, or 0 when Shopify sends none. Cart JSON leaves
    // the amount off some applications (a free-shipping discount has nothing to
    // allocate until checkout), and `undefined / 100` printed "-USD NaN".
    const discountCents = (amount, amountSet) => {
      const cents = Number(amount);
      if (amount !== null && amount !== '' && Number.isFinite(cents)) return cents;
      const decimal = Number(amountSet?.presentment_money?.amount);
      return Number.isFinite(decimal) ? Math.round(decimal * 100) : 0;
    };

    const setDiscountStatus = (scope, message, isError) => {
      const status = scope?.querySelector('[data-discount-status]');
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      status.classList.toggle('is-error', Boolean(isError));
    };

    const readCart = async () => {
      const response = await fetch(`${ROUTES.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart request failed');
      return response.json();
    };

    const setCartDiscounts = async (codes) => {
      const response = await fetch(`${ROUTES.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      if (!response.ok) throw new Error('discount update failed');
      const cart = await response.json();
      if (!Array.isArray(cart?.discount_codes)) throw new Error('unexpected discount response');
      return cart;
    };

    // What each code took off the cart, keyed by lowercased code. A code can be
    // accepted (applicable: true) and still have no allocation yet - free
    // shipping, for one, only resolves at checkout once there is an address.
    const codeSavings = (cart) => {
      const savings = new Map();
      const add = (application, amount) => {
        if (!isCodeApplication(application) || !application.title) return;
        const key = String(application.title).toLowerCase();
        savings.set(key, (savings.get(key) || 0) + (amount || 0));
      };
      (cart?.cart_level_discount_applications || []).forEach((application) => {
        add(application, discountCents(application.total_allocated_amount, application.total_allocated_amount_set));
      });
      (cart?.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          add(allocation.discount_application, discountCents(allocation.amount, allocation.amount_set));
        });
      });
      return savings;
    };

    // One pill per code the shopper entered, with what it saved and a remove
    // button. Automatic discounts are not listed here: they have no code and
    // cannot be removed, so they are named on the lines and in the totals.
    // Built from DOM nodes: the code is shopper-entered text. The cart page
    // renders the same markup in Liquid (sections/main-cart.liquid).
    const renderDiscountCodes = (list, cart) => {
      if (!list) return;
      const savings = codeSavings(cart);
      list.textContent = '';
      cartCodes(cart).forEach((code) => {
        const item = document.createElement('li');
        item.className = 'cart-discount-code';
        item.dataset.discountCode = code;

        const label = document.createElement('span');
        label.className = 'cart-discount-code__label';
        label.textContent = code;
        item.appendChild(label);

        const saved = savings.get(code.toLowerCase()) || 0;
        const detail = document.createElement('span');
        if (saved > 0) {
          detail.className = 'cart-discount-code__amount';
          detail.textContent = `\u2212${moneyFmt(saved)}`;
        } else {
          item.classList.add('is-pending');
          detail.className = 'cart-discount-code__note';
          detail.textContent = DISCOUNT_TEXT.atCheckout;
        }
        item.appendChild(detail);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-discount-code__remove';
        button.dataset.discountRemove = code;
        button.setAttribute('aria-label', DISCOUNT_TEXT.remove.replace('__CODE__', code));
        button.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
        item.appendChild(button);

        list.appendChild(item);
      });
      list.hidden = list.children.length === 0;
    };

    const discountScopeOf = (el) => el?.closest('[data-discount-scope]');
    const inCartDrawer = (scope) => Boolean(scope?.closest('#mkt-cart-drawer'));

    // Hands the updated cart to whichever view the change came from. The cart
    // page is server-rendered, so it reloads and shows the status afterwards.
    const finishDiscountChange = (scope, cart, message, isError) => {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } }));
      const onCartPage = document.body.classList.contains('template-cart');
      if (inCartDrawer(scope) && typeof renderCartDrawer === 'function') {
        renderCartDrawer(cart);
        setDiscountStatus(scope, message, isError);
        if (!onCartPage) return;
      }
      try {
        window.sessionStorage.setItem(DISCOUNT_STATUS_KEY, JSON.stringify({ message, isError: Boolean(isError) }));
      } catch (_) {}
      window.location.reload();
    };

    const setDiscountBusy = (scope, busy) => {
      if (!scope) return;
      scope.classList.toggle('is-busy', busy);
      scope.setAttribute('aria-busy', String(busy));
      scope.querySelectorAll('button, input').forEach((control) => { control.disabled = busy; });
    };

    const applyDiscount = async (scope) => {
      const input = scope?.querySelector('[data-discount-input]');
      const code = input?.value.trim();
      if (!code || scope.classList.contains('is-busy')) return;

      setDiscountBusy(scope, true);
      setDiscountStatus(scope, DISCOUNT_TEXT.applying, false);

      let existing;
      let cart;
      try {
        existing = cartCodes(await readCart());
        if (existing.some((current) => sameCode(current, code))) {
          setDiscountBusy(scope, false);
          setDiscountStatus(scope, DISCOUNT_TEXT.alreadyApplied, false);
          input.value = '';
          input.focus();
          return;
        }
        cart = await setCartDiscounts([...existing, code]);
      } catch (_) {
        // The update endpoint did not answer with a cart: fall back to the
        // full-page discount route rather than silently doing nothing.
        window.location.href = `${shopRoot()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(ROUTES.cart)}`;
        return;
      }

      const entry = cart.discount_codes.find((item) => sameCode(item.code, code));
      if (!entry || !entry.applicable) {
        // Shopify keeps a rejected code on the cart. Put the previous codes back
        // so a typo does not follow the shopper to checkout as an error.
        try {
          cart = await setCartDiscounts(existing);
        } catch (_) {}
        setDiscountBusy(scope, false);
        if (inCartDrawer(scope) && typeof renderCartDrawer === 'function') renderCartDrawer(cart);
        setDiscountStatus(scope, DISCOUNT_TEXT.error, true);
        input.focus();
        input.select();
        return;
      }

      input.value = '';
      const reduced = (codeSavings(cart).get(String(entry.code).toLowerCase()) || 0) > 0;
      setDiscountBusy(scope, false);
      finishDiscountChange(scope, cart, reduced ? DISCOUNT_TEXT.applied : DISCOUNT_TEXT.pending, false);
    };

    const removeDiscount = async (button) => {
      const scope = discountScopeOf(button);
      const code = button.dataset.discountRemove;
      if (!scope || !code || scope.classList.contains('is-busy')) return;

      const pill = button.closest('[data-discount-code]');
      const list = pill?.parentElement;
      // Where focus goes once the pill is gone: the next pill, else the field.
      const nextCode = (pill?.nextElementSibling || pill?.previousElementSibling)?.dataset.discountCode;

      pill?.classList.add('is-removing');
      setDiscountBusy(scope, true);
      setDiscountStatus(scope, DISCOUNT_TEXT.removing, false);

      try {
        const remaining = cartCodes(await readCart()).filter((current) => !sameCode(current, code));
        const cart = await setCartDiscounts(remaining);
        if (cartCodes(cart).some((current) => sameCode(current, code))) throw new Error('discount still applied');
        setDiscountBusy(scope, false);
        finishDiscountChange(scope, cart, DISCOUNT_TEXT.removed, false);
        const nextButton = nextCode
          ? [...(list?.querySelectorAll('[data-discount-remove]') || [])].find((el) => sameCode(el.dataset.discountRemove, nextCode))
          : null;
        (nextButton || scope.querySelector('[data-discount-input]'))?.focus();
      } catch (_) {
        pill?.classList.remove('is-removing');
        setDiscountBusy(scope, false);
        setDiscountStatus(scope, DISCOUNT_TEXT.removeError, true);
        button.focus();
      }
    };

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-discount-form]');
      if (!form) return;
      event.preventDefault();
      applyDiscount(discountScopeOf(form));
    });

    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-discount-remove]');
      if (removeButton) {
        event.preventDefault();
        removeDiscount(removeButton);
        return;
      }
      const button = event.target.closest('[data-discount-submit]');
      if (!button) return;
      event.preventDefault();
      applyDiscount(discountScopeOf(button));
    });

    // The cart-page field sits inside the cart form, so Enter used to submit that
    // form and run straight to checkout.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest('[data-discount-input]');
      if (!input) return;
      event.preventDefault();
      applyDiscount(discountScopeOf(input));
    });

    // Cart page: Liquid can only list codes that reduced the cart, so refresh the
    // pills from the cart JSON to include codes waiting for checkout, and show
    // the status carried over from the reload that followed a change.
    document.querySelectorAll('[data-discount-scope]').forEach((scope) => {
      if (inCartDrawer(scope)) return;
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(DISCOUNT_STATUS_KEY) || 'null');
        window.sessionStorage.removeItem(DISCOUNT_STATUS_KEY);
        if (saved?.message) setDiscountStatus(scope, saved.message, saved.isError);
      } catch (_) {}
      const list = scope.querySelector('[data-discount-codes]');
      if (list) readCart().then((cart) => renderDiscountCodes(list, cart)).catch(() => {});
    });

    // ── Overlay & scroll-lock helpers ──────────────────────────
    const overlay = document.getElementById('mkt-overlay');
    let overlayFor = null;

    const lockScroll = () => {
      document.body.style.overflow = 'hidden';
    };
    const unlockScroll = () => {
      document.body.style.overflow = '';
    };

    const showOverlay = (forEl) => {
      if (!overlay) return;
      overlayFor = forEl;
      overlay.classList.add('is-active');
      lockScroll();
    };
    const hideOverlay = () => {
      if (!overlay) return;
      overlay.classList.remove('is-active');
      overlayFor = null;
      unlockScroll();
    };

    if (overlay) {
      overlay.addEventListener('click', () => {
        closeNavDrawer();
        closeCartDrawer();
        closeSearchDialog();
        hideOverlay();
      });

      window.addEventListener('scroll', () => {
        const navIsOpen = drawer?.classList.contains('is-open');
        const cartIsOpen = cartDrawer?.classList.contains('is-open');
        const searchIsOpen = searchDialog && !searchDialog.hidden;
        if (!navIsOpen && !cartIsOpen && !searchIsOpen && overlay.classList.contains('is-active')) {
          hideOverlay();
        }
      }, { passive: true });
    }

    // ── Mobile nav drawer ──────────────────────────────────────
    const drawer = document.getElementById('mkt-drawer');
    const drawerClose = document.getElementById('mkt-drawer-close');

    const openNavDrawer = () => {
      if (!drawer) return;
      drawer.classList.add('is-open');
      showOverlay(drawer);
      drawerClose?.focus();
    };
    const closeNavDrawer = () => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      if (overlayFor === drawer) hideOverlay();
      // Every way of closing (overlay, Escape, close button) turns the X back
      // into the burger, not just the drawer's own close button.
      document.querySelectorAll('.mkt-burger[aria-expanded="true"]').forEach((burger) => burger.setAttribute('aria-expanded', 'false'));
    };

    // ── AJAX Cart drawer ───────────────────────────────────────
    const cartDrawer = document.getElementById('mkt-cart-drawer');
    const cartDrawerBody = document.getElementById('mkt-cart-drawer-body');
    const cartDrawerFooter = document.getElementById('mkt-cart-drawer-footer');
    const cartDrawerClose = document.getElementById('mkt-cart-drawer-close');
    const cartProgressWrap = cartDrawerFooter?.querySelector('[data-cart-progress-wrap]');
    const cartProgressText = cartDrawerFooter?.querySelector('[data-cart-progress-text]');
    const cartProgressBar = cartDrawerFooter?.querySelector('[data-cart-progress-bar]');
    const cartNoteWrap = cartDrawerFooter?.querySelector('[data-cart-note-wrap]');
    const cartNoteInput = cartDrawerFooter?.querySelector('[data-cart-note]');
    const cartUpsellWrap = cartDrawerFooter?.querySelector('[data-cart-upsell-wrap]');
    const cartUpsellLink = cartDrawerFooter?.querySelector('[data-cart-upsell-link]');
    const cartUpsellImage = cartDrawerFooter?.querySelector('[data-cart-upsell-image]');
    const cartUpsellPrice = cartDrawerFooter?.querySelector('[data-cart-upsell-price]');
    const cartUpsellAdd = cartDrawerFooter?.querySelector('[data-cart-upsell-add]');

    // Prices in the drawer come from cart.js as cents, so they are formatted
    // here rather than by Liquid's `money` filter. The format string is the
    // store's own, so the drawer prints what every Liquid price prints -
    // decimals, separators and where the symbol sits included.
    //
    // window.Shopify.currency.active is the ISO code, not the symbol, so
    // leaning on it alone printed "USD 44.00" next to the cart page's "$44.00".
    // It is still the right source for a currency switch: the shop's format
    // carries the shop's symbol, which is the wrong one once a shopper picks
    // another currency, and only the active code says which one they picked.
    const MONEY_FORMAT = CONFIG.moneyFormat;
    const SHOP_CURRENCY = CONFIG.shopCurrency;

    // [decimals, thousands separator, decimal separator] per Shopify placeholder.
    const MONEY_PLACEHOLDERS = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };

    const moneyFmt = (cents) => {
      const value = Number(cents);
      const amount = Number.isFinite(value) ? value : 0;
      const active = window.Shopify?.currency?.active;

      if (active && SHOP_CURRENCY && active !== SHOP_CURRENCY) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: active,
          }).format(amount / 100);
        } catch (error) {
          // An unknown code throws; the shop's own format is still better than nothing.
        }
      }

      return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
        const rule = MONEY_PLACEHOLDERS[name];
        if (!rule) return match;
        const [decimals, thousands, decimal] = rule;
        const parts = (amount / 100).toFixed(decimals).split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      });
    };

    // Cart line text is merchant/product data inserted with innerHTML, so it is
    // escaped: a quote in a title would otherwise break the markup.
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[char]);

    const cartDrawerSettings = () => ({
      threshold: parseInt(cartDrawer?.dataset.freeShippingThreshold || '0', 10) * 100,
      freeShippingEnabled: cartDrawer?.dataset.freeShippingEnabled === 'true',
      allowNotes: cartDrawer?.dataset.allowNotes === 'true',
      upsellEnabled: cartDrawer?.dataset.upsellEnabled === 'true',
      upsellId: cartDrawer?.dataset.upsellProductId || '',
      upsellUrl: cartDrawer?.dataset.upsellProductUrl || '',
      upsellPrice: parseInt(cartDrawer?.dataset.upsellProductPrice || '0', 10),
      upsellTitle: cartDrawer?.dataset.upsellProductTitle || '',
      upsellImage: cartDrawer?.dataset.upsellProductImage || '',
    });

    const updateDrawerMeta = (cart) => {
      const settings = cartDrawerSettings();
      if (cartProgressWrap) {
        if (!settings.freeShippingEnabled || !settings.threshold || settings.threshold <= 0) {
          cartProgressWrap.hidden = true;
        } else {
          const remaining = Math.max(settings.threshold - cart.total_price, 0);
          const pct = Math.min((cart.total_price / settings.threshold) * 100, 100);
          cartProgressWrap.hidden = false;
          if (cartProgressText) {
            cartProgressText.textContent = remaining > 0
              ? TEXT.freeShippingRemaining.replace('AMOUNT_PLACEHOLDER', moneyFmt(remaining))
              : TEXT.freeShippingUnlocked;
          }
          if (cartProgressBar) cartProgressBar.style.width = `${pct}%`;
        }
      }
      if (cartNoteWrap) {
        cartNoteWrap.hidden = !settings.allowNotes;
        if (cartNoteInput && typeof cart.note === 'string') cartNoteInput.value = cart.note;
      }
      if (cartUpsellWrap) {
        const shouldShow = settings.upsellEnabled && settings.upsellId;
        cartUpsellWrap.hidden = !shouldShow;
        if (shouldShow) {
          if (cartUpsellLink) {
            cartUpsellLink.textContent = settings.upsellTitle || TEXT.recommendedProduct;
            cartUpsellLink.href = settings.upsellUrl || '#';
          }
          if (cartUpsellImage) {
            if (settings.upsellImage) {
              cartUpsellImage.src = settings.upsellImage;
              cartUpsellImage.alt = settings.upsellTitle || '';
              cartUpsellImage.hidden = false;
            } else {
              cartUpsellImage.hidden = true;
            }
          }
          if (cartUpsellPrice) cartUpsellPrice.textContent = moneyFmt(settings.upsellPrice || 0);
        }
      }
    };

    const updateCartNote = async (note) => {
      try {
        const r = await fetch(`${ROUTES.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        });
        const cart = await r.json();
        renderCartDrawer(cart);
      } catch (_) {}
    };

    const addUpsell = async () => {
      const settings = cartDrawerSettings();
      if (!settings.upsellId) return;
      try {
        const r = await fetch(`${ROUTES.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: settings.upsellId, quantity: 1 }),
        });
        if (!r.ok) return;
        const cart = await cartFetch();
        if (cart) renderCartDrawer(cart);
      } catch (_) {}
    };

    // "Cart · N items" text beside the cart icon (labels come from the rendered markup).
    const setItemsLabel = (count) => {
      document.querySelectorAll('[data-cart-items-label]').forEach((el) => {
        const one = el.getAttribute('data-label-one');
        const other = el.getAttribute('data-label-other');
        if (one && other) el.textContent = (count === 1 ? one : other).replace('__COUNT__', String(count));
      });
    };

    const renderCartDrawer = (cart) => {
      // Only ever render a real cart; an API error object has no item_count.
      if (!cartDrawerBody || typeof cart?.item_count !== 'number') return;
      const countEls = document.querySelectorAll('[data-cart-count],[data-cart-drawer-count]');
      countEls.forEach((el) => {
        el.textContent = String(cart.item_count);
        el.classList.remove('is-bumping');
        void el.offsetWidth;
        el.classList.add('is-bumping');
        if (el.dataset.cartCount !== undefined) el.dataset.count = String(cart.item_count);
      });
      setItemsLabel(cart.item_count);

      if (cart.item_count === 0) {
        cartDrawerBody.innerHTML = `
          <div class="mkt-cart-empty">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2.5 3.5h2.6l2.5 12h11l2.4-9H6.1"/></svg>
            <p>${escapeHtml(TEXT.emptyTitle)}</p>
            <a class="btn" href="${escapeHtml(ROUTES.allProducts)}">${escapeHtml(TEXT.continueShopping)}</a>
          </div>`;
        if (cartDrawerFooter) cartDrawerFooter.style.display = 'none';
        return;
      }

      let html = '';
      cart.items.forEach((item) => {
        const title = escapeHtml(item.product_title);
        // A line with no image and no Theme settings default image used to
        // render <img src=""> - a broken-image icon with the alt text spilling out.
        const imageSrc = item.image ? item.image.replace('_240x240', '_120x120') : (cartDrawer?.dataset.defaultImage || '');
        const media = imageSrc
          ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml((item.featured_image && item.featured_image.alt) || item.product_title)}" loading="lazy">`
          : `<span class="mkt-cart-item__placeholder" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/></svg></span>`;
        // Product-level discounts (most automatic discounts: amount off products,
        // buy X get Y, collection offers) sit on the line, not the cart, so the
        // drawer used to show only a quietly lower price. Show what it was, the
        // discounted price, and which discount did it.
        // Codes are shown (with their amount) in the pills under the code field.
        const allocations = (item.line_level_discount_allocations || []).filter(
          (allocation) => discountCents(allocation.amount, allocation.amount_set) > 0 && !isCodeApplication(allocation.discount_application)
        );
        const originalLinePrice = item.original_line_price ?? item.final_line_price;
        const isDiscounted = originalLinePrice > item.final_line_price;
        const price = isDiscounted
          ? `<span class="visually-hidden">${escapeHtml(TEXT.originalPrice)}</span><s class="mkt-cart-item__compare">${moneyFmt(originalLinePrice)}</s>
             <span class="visually-hidden">${escapeHtml(TEXT.discountedPrice)}</span><span class="mkt-cart-item__final">${moneyFmt(item.final_line_price)}</span>`
          : moneyFmt(item.final_line_price);
        const discounts = allocations.length
          ? `<ul class="mkt-cart-item__discounts" role="list">${allocations.map((allocation) => `
              <li><span>${escapeHtml(allocation.discount_application?.title || TEXT.discountsTitle)}</span><span>&minus;${moneyFmt(discountCents(allocation.amount, allocation.amount_set))}</span></li>`).join('')}
            </ul>`
          : '';
        // Subscription plan name, e.g. "Deliver every 2 months". Present on the
        // /cart.js payload whenever the line was added with a selling plan.
        const sellingPlan =
          (item.selling_plan_allocation &&
            item.selling_plan_allocation.selling_plan &&
            item.selling_plan_allocation.selling_plan.name) || '';
        // Unit price, e.g. "$1.20/100g", same format as unit_price_with_measurement.
        // A reference value of 1 is left out ("/kg", not "/1kg"). The money part is
        // not escaped, like the other prices here: money formats can hold entities.
        const measurement = item.unit_price_measurement;
        const unitPrice = measurement && item.unit_price != null
          ? `${moneyFmt(item.unit_price)}/${escapeHtml(`${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit}`)}`
          : '';
        html += `
          <div class="mkt-cart-item" data-key="${escapeHtml(item.key)}">
            ${media}
            <div class="mkt-cart-item__info">
              <p class="mkt-cart-item__title"><a href="${escapeHtml(item.url)}">${title}</a></p>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="mkt-cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
              ${sellingPlan ? `<p class="mkt-cart-item__selling-plan">${escapeHtml(sellingPlan)}</p>` : ''}
              <p class="mkt-cart-item__price${isDiscounted ? ' is-discounted' : ''}">${price}</p>
              ${unitPrice ? `<small class="unit-price"><span class="visually-hidden">${escapeHtml(TEXT.unitPriceLabel)}</span>${unitPrice}</small>` : ''}
              ${discounts}
              <div class="mkt-cart-item__qty">
                <button type="button" class="mkt-qty-dec" data-key="${escapeHtml(item.key)}" data-qty="${item.quantity - 1}" aria-label="${escapeHtml(TEXT.decrease)}">&minus;</button>
                <span>${item.quantity}</span>
                <button type="button" class="mkt-qty-inc" data-key="${escapeHtml(item.key)}" data-qty="${item.quantity + 1}" aria-label="${escapeHtml(TEXT.increase)}">+</button>
              </div>
            </div>
            <button type="button" class="mkt-cart-item__remove" data-key="${escapeHtml(item.key)}" aria-label="${escapeHtml(TEXT.remove)} ${title}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>`;
      });
      cartDrawerBody.innerHTML = html;
      cartDrawerBody.classList.remove('is-refreshing');
      void cartDrawerBody.offsetWidth;
      cartDrawerBody.classList.add('is-refreshing');

      if (cartDrawerFooter) {
        cartDrawerFooter.style.display = '';
        const sub = cartDrawerFooter.querySelector('[data-cart-drawer-subtotal]');
        if (sub) sub.textContent = moneyFmt(cart.total_price);

        // Everything taken off, automatic and code, line and order level.
        const savings = cartDrawerFooter.querySelector('[data-cart-drawer-savings]');
        if (savings) {
          const saved = cart.total_discount || 0;
          const savingsAmount = savings.querySelector('[data-cart-drawer-savings-amount]');
          if (savingsAmount) savingsAmount.textContent = moneyFmt(saved);
          savings.hidden = saved <= 0;
        }

        // Automatic order discounts, so the drawer says why the total moved.
        // Codes are listed in the pills instead. Built with DOM nodes rather
        // than innerHTML: the title is merchant text.
        const discountList = cartDrawerFooter.querySelector('[data-cart-drawer-discounts]');
        if (discountList) {
          const applications = (cart.cart_level_discount_applications || []).filter((application) => !isCodeApplication(application));
          discountList.textContent = '';
          applications.forEach((application) => {
            const row = document.createElement('li');
            const title = document.createElement('span');
            title.textContent = application.title;
            const amount = document.createElement('span');
            let cents = discountCents(application.total_allocated_amount, application.total_allocated_amount_set);
            if (cents <= 0) {
              // No total on the application: add up what each line reports for it.
              cents = (cart.items || []).reduce((sum, item) => sum + (item.discounts || [])
                .filter((discount) => discount.title === application.title)
                .reduce((lineSum, discount) => lineSum + discountCents(discount.amount), 0), 0);
            }
            // No amount yet (free shipping and other checkout-only discounts):
            // name it without inventing a figure.
            amount.textContent = cents > 0 ? `\u2212${moneyFmt(cents)}` : DISCOUNT_TEXT.atCheckout;
            row.append(title, amount);
            discountList.appendChild(row);
          });
          discountList.hidden = applications.length === 0;
        }

        // Codes on the cart, each removable.
        renderDiscountCodes(cartDrawerFooter.querySelector('[data-discount-codes]'), cart);
      }
      updateDrawerMeta(cart);

      // Qty / remove buttons. Only the buttons: the item row also carries
      // data-key, and a listener there turned every click in the row (and the
      // bubble from these buttons) into a second "set to 0" request.
      cartDrawerBody.querySelectorAll('button[data-key]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          const key = btn.dataset.key;
          const qty = btn.dataset.qty !== undefined ? parseInt(btn.dataset.qty, 10) : 0;
          await cartChange(key, qty);
        });
      });
    };

    const cartFetch = async () => {
      try {
        const r = await fetch(`${ROUTES.cart}.js`);
        return await r.json();
      } catch (_) { return null; }
    };
    const cartChange = async (key, quantity) => {
      try {
        const r = await fetch(`${ROUTES.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        // A refused change (e.g. a line that is already gone) answers with an
        // error object, not a cart: re-read the real cart instead of rendering it.
        const cart = r.ok ? await r.json() : await cartFetch();
        if (!cart || typeof cart.item_count !== 'number') return;
        renderCartDrawer(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } }));
        // The cart page is server-rendered. Refresh it after a drawer change so
        // its line items, totals, and empty state match the updated cart.
        if (document.body.classList.contains('template-cart')) {
          window.location.reload();
        }
      } catch (_) {}
    };

    const openCartDrawer = async () => {
      if (!cartDrawer) return;
      cartDrawer.classList.add('is-open');
      showOverlay(cartDrawer);
      const cartBtn = document.getElementById('mkt-cart-btn');
      cartBtn?.setAttribute('aria-expanded', 'true');
      const cart = await cartFetch();
      if (cart) renderCartDrawer(cart);
    };
    const closeCartDrawer = () => {
      if (!cartDrawer) return;
      cartDrawer.classList.remove('is-open');
      if (overlayFor === cartDrawer) hideOverlay();
      const cartBtn = document.getElementById('mkt-cart-btn');
      cartBtn?.setAttribute('aria-expanded', 'false');
    };

    // ── Search dialog (search_style = icon) ───────────────────
    const searchDialog = document.getElementById('mkt-search-dialog');
    let searchDialogTrigger = null;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const openSearchDialog = (trigger) => {
      if (!searchDialog || !searchDialog.hidden) return;
      closeNavDrawer();
      closeCartDrawer();
      searchDialogTrigger = trigger || null;
      searchDialog.hidden = false;
      showOverlay(searchDialog);
      searchDialogTrigger?.setAttribute('aria-expanded', 'true');
      const input = searchDialog.querySelector('input[name="q"]');
      window.requestAnimationFrame(() => {
        input?.focus({ preventScroll: true });
        input?.select();
      });
    };
    const closeSearchDialog = () => {
      if (!searchDialog || searchDialog.hidden) return;
      searchDialog.hidden = true;
      if (overlayFor === searchDialog) hideOverlay();
      searchDialogTrigger?.setAttribute('aria-expanded', 'false');
      searchDialogTrigger?.focus({ preventScroll: true });
      searchDialogTrigger = null;
    };

    if (searchDialog) {
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-search-open]');
        if (opener) { event.preventDefault(); openSearchDialog(opener); return; }
        if (event.target.closest('[data-search-close]')) closeSearchDialog();
      });
      // Keep Tab inside the dialog while it is open
      searchDialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...searchDialog.querySelectorAll(focusableSelector)].filter((el) => el.getClientRects().length > 0);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target?.contains(searchDialog)) closeSearchDialog();
      });
    }

    // ── Garage (vehicle selector) popover ─────────────────────
    document.querySelectorAll('[data-garage]').forEach((wrap) => {
      const toggle = wrap.querySelector('[data-garage-toggle]');
      const panel = wrap.querySelector('[data-garage-panel]');
      if (!toggle || !panel) return;
      // The button always keeps its "My Garage" label: the chosen vehicle shows
      // inside the panel and in the breadcrumb, not on the button.

      const open = () => {
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        if (window.VehicleFinder) window.VehicleFinder.activate(panel);
        const first = panel.querySelector('select:not([disabled]), button');
        if (first) first.focus({ preventScroll: true });
      };
      const close = () => {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      };

      toggle.addEventListener('click', () => (panel.hidden ? open() : close()));
      panel.querySelector('[data-garage-close]')?.addEventListener('click', () => { close(); toggle.focus(); });
      document.addEventListener('click', (event) => { if (!panel.hidden && event.target.isConnected && !wrap.contains(event.target)) close(); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !panel.hidden) { close(); toggle.focus(); } });
    });

    // ── Search dropdown: limit to one product type or vendor ───
    // The limit is appended as Shopify search syntax (`filler
    // product_type:"Body Fillers"`), which both the search page and predictive
    // search apply natively. Liquid reads it back out of search.terms to show
    // the typed text and reselect the option (snippets/search-scope-terms).
    const scopedQuery = (query, select) => {
      const value = select?.value;
      if (!value) return query;
      return `${query} ${select.dataset.scopeField}:"${value.replace(/"/g, '')}"`.trim();
    };

    // Themed listbox over the native select: the OS menu a <select> opens
    // cannot be styled. The select keeps the value (and works without JS).
    const CHECK_ICON = '<svg class="mkt-scope__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    const enhanceScopeSelect = (select) => {
      const wrap = select.closest('.mkt-search__cats');
      if (!wrap || wrap.classList.contains('is-enhanced')) return;
      const listId = `${select.id}-listbox`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mkt-scope__button';
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', listId);
      const label = document.querySelector(`label[for="${select.id}"]`)?.textContent.trim();
      const buttonText = document.createElement('span');
      button.appendChild(buttonText);

      const panel = document.createElement('ul');
      panel.className = 'mkt-scope__panel';
      panel.id = listId;
      panel.hidden = true;
      panel.tabIndex = -1;
      panel.setAttribute('role', 'listbox');
      if (label) panel.setAttribute('aria-label', label);

      const options = [...select.options].map((option, index) => {
        const item = document.createElement('li');
        item.className = 'mkt-scope__option';
        item.id = `${listId}-${index}`;
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.innerHTML = `<span></span>${CHECK_ICON}`;
        item.firstChild.textContent = option.textContent;
        panel.appendChild(item);
        return item;
      });

      let active = -1;
      const sync = () => {
        buttonText.textContent = select.selectedOptions[0]?.textContent || '';
        button.setAttribute('aria-label', label ? `${label}: ${buttonText.textContent}` : buttonText.textContent);
        options.forEach((item, index) => item.setAttribute('aria-selected', String(index === select.selectedIndex)));
      };
      const setActive = (index) => {
        active = Math.max(0, Math.min(options.length - 1, index));
        options.forEach((item, i) => item.classList.toggle('is-active', i === active));
        panel.setAttribute('aria-activedescendant', options[active].id);
        options[active].scrollIntoView({ block: 'nearest' });
      };
      // Fixed to the viewport (the search form clips overflow). Opens upward
      // when there is more room above, e.g. a phone in landscape, and never
      // runs past either screen edge or the visible height.
      const GAP = 8;
      const EDGE = 16;
      const place = () => {
        const rect = button.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const below = viewportHeight - rect.bottom - GAP - EDGE;
        const above = rect.top - GAP - EDGE;
        const upward = below < 200 && above > below;
        panel.style.maxHeight = `${Math.max(120, Math.min(360, upward ? above : below))}px`;
        panel.style.top = upward ? 'auto' : `${Math.round(rect.bottom + GAP)}px`;
        panel.style.bottom = upward ? `${Math.round(window.innerHeight - rect.top + GAP)}px` : 'auto';
        const maxLeft = window.innerWidth - panel.offsetWidth - EDGE;
        panel.style.left = `${Math.round(Math.max(EDGE, Math.min(rect.left, maxLeft)))}px`;
      };
      const isOpen = () => !panel.hidden;
      const close = (focusButton = false) => {
        if (!isOpen()) return;
        panel.hidden = true;
        wrap.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        if (focusButton) button.focus();
      };
      const open = () => {
        panel.hidden = false;
        wrap.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        place();
        setActive(select.selectedIndex);
        panel.focus({ preventScroll: true });
      };
      const choose = (index) => {
        if (select.selectedIndex !== index) {
          select.selectedIndex = index;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        sync();
        close(true);
      };

      button.addEventListener('click', () => (isOpen() ? close() : open()));
      button.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          open();
        }
      });
      panel.addEventListener('click', (event) => {
        const item = event.target.closest('.mkt-scope__option');
        if (item) choose(options.indexOf(item));
      });
      panel.addEventListener('mousemove', (event) => {
        const item = event.target.closest('.mkt-scope__option');
        if (item && options.indexOf(item) !== active) setActive(options.indexOf(item));
      });
      let typed = '';
      let typedTimer = null;
      panel.addEventListener('keydown', (event) => {
        const keys = {
          ArrowDown: () => setActive(active + 1),
          ArrowUp: () => setActive(active - 1),
          Home: () => setActive(0),
          End: () => setActive(options.length - 1),
          PageDown: () => setActive(active + 8),
          PageUp: () => setActive(active - 8),
          Enter: () => choose(active),
          ' ': () => choose(active),
          Escape: () => close(true),
          Tab: () => close(),
        };
        if (keys[event.key]) {
          if (event.key !== 'Tab') event.preventDefault();
          keys[event.key]();
          return;
        }
        // Type-ahead: jump to the first option starting with the typed letters.
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
          typed += event.key.toLowerCase();
          window.clearTimeout(typedTimer);
          typedTimer = window.setTimeout(() => { typed = ''; }, 600);
          const match = options.findIndex((item) => item.textContent.trim().toLowerCase().startsWith(typed));
          if (match >= 0) setActive(match);
        }
      });
      document.addEventListener('click', (event) => {
        if (isOpen() && !wrap.contains(event.target) && !panel.contains(event.target)) close();
      });
      // Follow the button while open. Only a width change (rotation, window
      // resize) closes it: on phones the keyboard hiding changes the height,
      // and a hide-on-scroll header moving the button out of view also closes.
      let lastWidth = window.innerWidth;
      let frame = 0;
      const follow = () => {
        if (!isOpen() || frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          const rect = button.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) close();
          else place();
        });
      };
      window.addEventListener('scroll', follow, { passive: true });
      window.addEventListener('resize', () => {
        if (window.innerWidth !== lastWidth) {
          lastWidth = window.innerWidth;
          close();
        } else {
          follow();
        }
      });
      window.visualViewport?.addEventListener('resize', follow);
      select.addEventListener('change', sync);

      select.after(button);
      wrap.appendChild(panel);
      wrap.classList.add('is-enhanced');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      sync();
    };

    document.querySelectorAll('[data-search-scope-select]').forEach((select) => {
      const form = select.closest('form');
      const input = form?.querySelector('input[name="q"], input[data-search-query]');
      if (!form || !input) return;

      select.addEventListener('change', () => {
        select.dispatchEvent(new CustomEvent('search:scope-change', { bubbles: true }));
      });
      enhanceScopeSelect(select);

      // Submit the combined query from a hidden field so the visible box keeps
      // only what the shopper typed (also when they come back with Back).
      form.addEventListener('submit', () => {
        form.querySelector('[data-search-scoped-q]')?.remove();
        input.setAttribute('name', 'q');
        input.removeAttribute('data-search-query');
        if (!select.value) return;
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'q';
        hidden.value = scopedQuery(input.value, select);
        hidden.setAttribute('data-search-scoped-q', '');
        form.appendChild(hidden);
        input.removeAttribute('name');
        input.setAttribute('data-search-query', '');
        if (!form.querySelector('input[name="type"]')) {
          const type = document.createElement('input');
          type.type = 'hidden';
          type.name = 'type';
          type.value = 'product';
          type.setAttribute('data-search-scoped-q', '');
          form.appendChild(type);
        }
      });
      window.addEventListener('pageshow', () => {
        form.querySelectorAll('[data-search-scoped-q]').forEach((el) => el.remove());
        input.setAttribute('name', 'q');
        input.removeAttribute('data-search-query');
      });
    });

    // ── Predictive search ─────────────────────────────────────
    document.querySelectorAll('[data-predictive]').forEach((wrap) => {
      const input = wrap.querySelector('input[name="q"]');
      const scopeSelect = wrap.querySelector('[data-search-scope-select]');
      const results = wrap.querySelector('[data-predictive-results]');
      const backdrop = wrap.querySelector('[data-predictive-backdrop]');
      if (!input || !results) return;
      const endpoint = wrap.dataset.predictiveUrl || ROUTES.predictiveSearch;
      const types = wrap.dataset.predictiveTypes || 'product';
      const limit = parseInt(wrap.dataset.predictiveLimit || '6', 10);
      const isModal = wrap.dataset.predictiveStyle === 'modal';
      let timer = null;
      let controller = null;
      let lastQuery = '';

      const positionModal = () => {
        if (!isModal) return;
        const top = Math.round(input.getBoundingClientRect().bottom + 12);
        results.style.top = `${top}px`;
        wrap.style.setProperty('--mkt-predictive-top', `${top}px`);
      };
      const open = () => {
        if (!results.innerHTML.trim()) return;
        positionModal();
        results.hidden = false;
        if (backdrop) backdrop.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        wrap.classList.add('is-open');
      };
      const close = () => {
        results.hidden = true;
        if (backdrop) backdrop.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        wrap.classList.remove('is-open');
      };

      const search = async (query) => {
        if (query.length < 2) { close(); results.innerHTML = ''; lastQuery = ''; return; }
        if (query === lastQuery) { open(); return; }
        if (controller) controller.abort();
        controller = new AbortController();
        wrap.classList.add('is-loading');
        try {
          const params = new URLSearchParams({
            q: scopedQuery(query, scopeSelect),
            'resources[type]': types,
            'resources[limit]': String(limit),
            'resources[limit_scope]': 'each',
            'resources[options][unavailable_products]': 'last',
            'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
            section_id: 'predictive-search',
          });
          const response = await fetch(`${endpoint}?${params}`, { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(String(response.status));
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-predictive-content]');
          results.innerHTML = content ? content.outerHTML : '';
          lastQuery = query;
          if (content) open(); else close();
        } catch (error) {
          if (error.name !== 'AbortError') close();
        } finally {
          wrap.classList.remove('is-loading');
        }
      };

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => search(input.value.trim()), 220);
      });
      input.addEventListener('focus', () => { if (input.value.trim().length >= 2) search(input.value.trim()); });
      // A new limit invalidates the cached suggestions for the same text.
      wrap.addEventListener('search:scope-change', () => {
        lastQuery = '';
        if (input.value.trim().length >= 2) search(input.value.trim());
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { close(); return; }
        if (event.key === 'ArrowDown' && !results.hidden) {
          event.preventDefault();
          results.querySelector('[data-predictive-item]')?.focus();
        }
      });
      // Result-type tabs (rendered by the predictive-search section, shown in the dialog layout)
      const selectTab = (tab) => {
        const tabs = [...results.querySelectorAll('[data-psr-tab]')];
        tabs.forEach((el) => {
          const active = el === tab;
          el.setAttribute('aria-selected', active ? 'true' : 'false');
          el.tabIndex = active ? 0 : -1;
        });
        results.querySelectorAll('[data-psr-panel]').forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.psrPanel === tab.dataset.psrTab);
        });
      };
      results.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-psr-tab]');
        if (tab) selectTab(tab);
      });

      results.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-psr-tab]');
        if (tab) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const tabs = [...results.querySelectorAll('[data-psr-tab]')];
          const step = event.key === 'ArrowRight' ? 1 : -1;
          const next = tabs.at((tabs.indexOf(tab) + step) % tabs.length);
          if (next) { selectTab(next); next.focus(); }
          return;
        }
        // Only items in the visible tab panel are navigable
        const items = [...results.querySelectorAll('[data-predictive-item]')].filter((el) => el.getClientRects().length > 0);
        const index = items.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') { event.preventDefault(); (items[index + 1] || items[0])?.focus(); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); if (index <= 0) input.focus(); else items[index - 1].focus(); }
        else if (event.key === 'Escape') { close(); input.focus(); }
      });

      document.addEventListener('click', (event) => { if (!results.hidden && !wrap.contains(event.target)) close(); });
      if (backdrop) backdrop.addEventListener('click', close);
      window.addEventListener('resize', () => { if (!results.hidden) positionModal(); });
    });

    // ── Categories side panel (menu style V1) ─────────────────
    document.querySelectorAll('[data-cats-panel-root]').forEach((root) => {
      const trigger = root.querySelector('[data-cats-panel-open]');
      const drawer = root.querySelector('.mkt-cats-drawer');
      if (!trigger || !drawer) return;
      const subs = [...drawer.querySelectorAll('[data-cats-sub]')];
      const items = [...drawer.querySelectorAll('[data-cats-item]')];
      let lastFocus = null;

      const showSub = (link) => {
        const id = link?.dataset.catsTarget;
        items.forEach((item) => {
          const a = item.querySelector('[data-cats-target]');
          const active = !!a && a === link;
          item.classList.toggle('is-active', active);
          if (a) a.setAttribute('aria-expanded', String(active));
        });
        subs.forEach((sub) => { sub.hidden = sub.id !== id; });
      };
      const open = () => {
        lastFocus = document.activeElement;
        drawer.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        showSub(null);
        drawer.querySelector('.mkt-cats-drawer__close')?.focus();
      };
      const close = () => {
        drawer.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        showSub(null);
        if (lastFocus) lastFocus.focus();
      };

      trigger.addEventListener('click', () => (drawer.hidden ? open() : close()));
      drawer.querySelectorAll('[data-cats-panel-close]').forEach((el) => el.addEventListener('click', close));
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); close(); }
      });
      items.forEach((item) => {
        const link = item.querySelector('[data-cats-target]');
        item.addEventListener('mouseenter', () => showSub(link));
        item.addEventListener('focusin', () => showSub(link));
        if (link) {
          link.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              showSub(link);
              document.getElementById(link.dataset.catsTarget)?.querySelector('a')?.focus();
            }
          });
        }
      });
      subs.forEach((sub) => {
        sub.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            drawer.querySelector('[data-cats-item].is-active [data-cats-target]')?.focus();
          }
        });
      });
    });

    // ── Init per header instance ───────────────────────────────
    const initMktHeader = () => {
      const header = document.getElementById('mkt-header');
      if (!header || header.dataset.init === 'true') return;
      header.dataset.init = 'true';

      const closeItem = (item) => {
        item.classList.remove('is-open');
        item.querySelectorAll('[aria-expanded]').forEach((control) => control.setAttribute('aria-expanded', 'false'));
        item.querySelectorAll('[data-flyout-item].is-open').forEach((flyout) => flyout.classList.remove('is-open', 'is-reversed'));
      };
      const closeAll = (except) => {
        header.querySelectorAll('[data-nav-item].is-open').forEach((i) => {
          if (i !== except) closeItem(i);
        });
      };

      // Desktop nav items: hover with delay + keyboard + click
      header.querySelectorAll('[data-nav-item]').forEach((item) => {
        const trigger = item.querySelector('[aria-haspopup]');
        if (!trigger) return;
        const delay = parseInt(item.dataset.hoverDelay || '150', 10);
        const openBehavior = item.dataset.openBehavior || 'hover';
        let t;
        item.addEventListener('mouseenter', () => {
          if (openBehavior === 'click') return;
          clearTimeout(t);
          closeAll(item);
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        });
        item.addEventListener('mouseleave', () => {
          if (openBehavior === 'click') return;
          t = setTimeout(() => closeItem(item), delay);
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) closeItem(item);
        });
        if (trigger.tagName === 'BUTTON') {
          trigger.addEventListener('click', () => {
            const open = item.classList.toggle('is-open');
            trigger.setAttribute('aria-expanded', String(open));
            if (open) closeAll(item);
          });
        } else {
          trigger.addEventListener('click', (event) => {
            const needsToggle = openBehavior === 'click' || window.matchMedia('(hover: none)').matches;
            if (!needsToggle || item.classList.contains('is-open')) return;
            event.preventDefault();
            closeAll(item);
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          });
        }
        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            closeAll(item);
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
            item.querySelector('.mkt-dropdown a, .mkt-mega a')?.focus();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            closeItem(item);
            trigger.focus();
          }
        });
      });

      // Third-level dropdown flyouts: separate toggles keep parent links usable.
      header.querySelectorAll('.mkt-flyout-toggle').forEach((toggle) => {
        const flyoutItem = toggle.closest('[data-flyout-item]');
        const flyout = flyoutItem?.querySelector(':scope > .mkt-dropdown__flyout');
        if (!flyoutItem || !flyout) return;

        const positionFlyout = () => {
          if (!flyoutItem.classList.contains('has-flyout--auto')) return;
          flyoutItem.classList.remove('is-reversed');
          const bounds = flyout.getBoundingClientRect();
          if (bounds.right > window.innerWidth - 12) flyoutItem.classList.add('is-reversed');
        };

        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const open = flyoutItem.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', String(open));
          if (open) positionFlyout();
        });
        toggle.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            flyoutItem.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            positionFlyout();
            flyout.querySelector('a')?.focus();
          }
          if (event.key === 'Escape') {
            flyoutItem.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            toggle.focus();
          }
        });
      });

      // Burger → open left drawer
      const burger = header.querySelector('.mkt-burger');
      burger?.addEventListener('click', () => {
        openNavDrawer();
        burger.setAttribute('aria-expanded', 'true');
      });

      // Drawer close button
      drawerClose?.addEventListener('click', () => {
        closeNavDrawer();
        burger?.setAttribute('aria-expanded', 'false');
        burger?.focus();
      });

      // Drawer accordions (multi-level)
      if (drawer) {
        drawer.querySelectorAll('.mkt-drawer__toggle').forEach((toggle) => {
          toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const head = toggle.closest('.mkt-drawer__head');
            const sub = head ? head.nextElementSibling : null;
            if (!sub || !sub.classList.contains('mkt-drawer__sub')) return;
            const open = sub.classList.toggle('is-open');
            toggle.setAttribute('aria-expanded', String(open));
          });
        });
      }

      // Cart drawer button
      const cartBtn = document.getElementById('mkt-cart-btn');
      // The trigger is a link to /cart (so apps can find it and it works
      // without JavaScript); open the drawer instead of following it.
      cartBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        openCartDrawer();
      });
      cartDrawerClose?.addEventListener('click', () => { closeCartDrawer(); cartBtn?.focus(); });
      cartNoteInput?.addEventListener('change', () => updateCartNote(cartNoteInput.value));
      cartUpsellAdd?.addEventListener('click', addUpsell);

      // Badges
      const setCount = (el, n) => {
        if (!el) return;
        el.textContent = String(n);
        el.dataset.count = String(n);
      };
      const readStore = (key) => {
        try {
          const v = JSON.parse(localStorage.getItem(key) || '[]');
          return Array.isArray(v) ? v.length : 0;
        } catch (_) { return 0; }
      };
      const updateBadges = () => {
        setCount(header.querySelector('[data-compare-count]'), readStore('compare_vehicles'));
      };
      updateBadges();
      window.addEventListener('storage', updateBadges);
      document.addEventListener('compare:updated', updateBadges);
      document.addEventListener('cart:updated', (e) => {
        if (typeof e.detail?.item_count === 'number') {
          setCount(header.querySelector('[data-cart-count]'), e.detail.item_count);
          setItemsLabel(e.detail.item_count);
        }
      });

      // Scroll elevation / hide-on-scroll-down
      const behavior = header.dataset.stickyBehavior || 'always';
      let lastY = window.scrollY;
      const onScroll = () => {
        const y = window.scrollY;
        header.classList.toggle('is-scrolled', y > 4);
        if (behavior === 'scroll-up') {
          header.classList.toggle('is-hidden', y > lastY && y > header.offsetHeight);
        }
        lastY = y;
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    };

    // ── Global once: Escape key ────────────────────────────────
    if (!document.documentElement.dataset.mktDelegates) {
      document.documentElement.dataset.mktDelegates = 'true';
      document.addEventListener('click', (e) => {
        const header = document.getElementById('mkt-header');
        if (!header) return;
        header.querySelectorAll('[data-nav-item].is-open').forEach((item) => {
          if (!item.contains(e.target)) {
            item.classList.remove('is-open');
            item.querySelector('[aria-haspopup]')?.setAttribute('aria-expanded', 'false');
          }
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const header = document.getElementById('mkt-header');
        if (!header) return;
        const openSearch = document.getElementById('mkt-search-dialog');
        if (openSearch && !openSearch.hidden) { closeSearchDialog(); return; }
        const openNav = document.getElementById('mkt-drawer');
        if (openNav?.classList.contains('is-open')) { closeNavDrawer(); return; }
        const openCart = document.getElementById('mkt-cart-drawer');
        if (openCart?.classList.contains('is-open')) { closeCartDrawer(); return; }
        header.querySelectorAll('[data-nav-item].is-open').forEach((item) => {
          const trigger = item.querySelector('[aria-haspopup]');
          item.classList.remove('is-open');
          trigger?.setAttribute('aria-expanded', 'false');
          trigger?.focus();
        });
      });
    }

    document.addEventListener('DOMContentLoaded', initMktHeader);
    document.addEventListener('shopify:section:load', initMktHeader);
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const panel = block?.closest('[data-mega-menu]');
      const item = panel?.closest('[data-nav-item]');
      if (!item) return;
      item.closest('.mkt-header')?.querySelectorAll('[data-nav-item].is-open').forEach((openItem) => {
        if (openItem !== item) openItem.classList.remove('is-open');
      });
      item.classList.add('is-open');
      item.querySelector(':scope > [aria-haspopup]')?.setAttribute('aria-expanded', 'true');
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      event.target?.closest('[data-nav-item]')?.classList.remove('is-open');
    });
    if (document.readyState !== 'loading') initMktHeader();

  // ── Brand Sidebar Mega Menu (Harley style) ─────────────────────────────
  (() => {
    const initBrandSidebars = () => {
      document.querySelectorAll('.mkt-mega--brands').forEach(mega => {
        const sidebarBtns = mega.querySelectorAll('.mkt-brands-sidebar__link');
        const panels = mega.querySelectorAll('.mkt-brands-panel');
        sidebarBtns.forEach(btn => {
          btn.addEventListener('mouseenter', () => {
            const idx = btn.dataset.brandIdx;
            sidebarBtns.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected','false'); });
            panels.forEach(p => { p.classList.remove('is-active'); p.setAttribute('aria-hidden','true'); });
            btn.classList.add('is-active');
            btn.setAttribute('aria-selected','true');
            const target = mega.querySelector(`.mkt-brands-panel[data-brand-panel="${idx}"]`);
            if (target) { target.classList.add('is-active'); target.setAttribute('aria-hidden','false'); }
          });
          btn.addEventListener('click', () => btn.dispatchEvent(new Event('mouseenter')));
        });
      });
    };
    if (document.readyState !== 'loading') initBrandSidebars();
    else document.addEventListener('DOMContentLoaded', initBrandSidebars);
    document.addEventListener('shopify:section:load', initBrandSidebars);
  })();
  };

  runHeader();

  // The inline script used to re-run when the theme editor re-rendered the
  // header. Keep that: re-run once the new header markup is in place.
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.querySelector('#mkt-header-config')) runHeader();
    });
  }
})();
